import './DPCalendar.scss';
import * as Dfns from 'date-fns/fp';
import { useCallback, useMemo, useState } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import {
  addFiscalMonths,
  CompanyFiscalCal,
  differenceInFiscalMonths,
  getStartOfFiscalMonth,
  getStartOfFiscalMonthLabel,
  getStartOfFiscalYearLabel,
  subFiscalMonths
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { Appearance, Design } from '@src/Components/designConstants';
import { DATE_FORMAT } from '../../../utils/data';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { IconButton } from '../../IconButton';
import { DateRange, MonthInfo } from '../date-picker-types';
import { FOCUS_OPTIONS, YEAR_MONTH_FORMAT } from '../DatePickerButton/DatePickerConstants';
import { IndividualMonth } from '../IndividualMonth';
import { MonthGrid } from '../MonthGrid';
import { getMonthInfo } from './monthInfo';

const TODAY = Dfns.format(DATE_FORMAT, new Date());
export const format = Dfns.format(DATE_FORMAT);

interface DPCalendarProps {
  start?: string;
  end?: string;
  focus?: (typeof FOCUS_OPTIONS)[number];
  onChange: (dateRange: Partial<DateRange>, focus: (typeof FOCUS_OPTIONS)[number]) => void;
  isOutsideRange?: (date: string) => boolean;
  isDayBlocked?: (date: string, focus?: (typeof FOCUS_OPTIONS)[number]) => boolean;
  years: number[];
  currentMonthSecond?: boolean;
  maxDate?: string;
  comparison?: boolean;
  fiscalCalOverride?: CompanyFiscalCal;
  numVisibleMonths?: number;
  // Single-date mode: each click selects exactly one day (start === end) rather
  // than extending a range.
  isSingle?: boolean;
  // Renders numVisibleYears side-by-side year grids of month cells instead of the day grid —
  // structurally the same as the day view's side-by-side months, just one granularity up. Each
  // click selects a whole month; isOutsideRange/isDayBlocked are evaluated against each month's
  // start.
  fullMonthsOnly?: boolean;
  numVisibleYears?: number;
}

export const DPCalendar = ({
  start,
  end,
  focus,
  onChange,
  isOutsideRange = () => false,
  isDayBlocked = () => false,
  years,
  currentMonthSecond,
  maxDate,
  comparison = false,
  fiscalCalOverride,
  numVisibleMonths = 3,
  isSingle = false,
  fullMonthsOnly = false,
  numVisibleYears = 2
}: DPCalendarProps) => {
  const [selectedMonth, setSelectedMonth] = useState(start);
  const { fiscal_cal } = useCompanyInfo();
  const fiscalCal = useMemo(() => fiscalCalOverride || fiscal_cal, [fiscalCalOverride, fiscal_cal]);
  // Anchor so the start date's year lands in the LAST visible pane (rightmost) rather than the
  // first — with numVisibleYears panes shown left-to-right, that means starting numVisibleYears-1
  // years earlier, so e.g. today's year shows on the right with the prior year(s) to its left,
  // matching how these years are actually usable (nothing after today's year is selectable).
  const [selectedYear, setSelectedYear] = useState(() => {
    const startYear = parseInt(
      Dfns.format('yyyy', getStartOfFiscalYearLabel(Dfns.parseISO(start || TODAY), fiscalCal)),
      10
    );
    return startYear - (numVisibleYears - 1);
  });
  const { monthInfo, visibleMonthDigits } = useMemo(() => {
    let parsedStart = Dfns.parseISO(selectedMonth || TODAY);
    let nextMonth = addFiscalMonths(1, parsedStart, fiscalCal);
    let thirdMonth = addFiscalMonths(2, parsedStart, fiscalCal);

    if (currentMonthSecond) {
      thirdMonth = nextMonth;
      nextMonth = parsedStart;
      parsedStart = subFiscalMonths(1, parsedStart, fiscalCal);
    }

    const monthDigits = new Set<string>();
    monthDigits.add(
      Dfns.format(YEAR_MONTH_FORMAT, getStartOfFiscalMonthLabel(parsedStart, fiscalCal))
    );
    monthDigits.add(
      Dfns.format(YEAR_MONTH_FORMAT, getStartOfFiscalMonthLabel(nextMonth, fiscalCal))
    );
    monthDigits.add(
      Dfns.format(YEAR_MONTH_FORMAT, getStartOfFiscalMonthLabel(thirdMonth, fiscalCal))
    );

    const info = [
      getMonthInfo(parsedStart, fiscalCal),
      getMonthInfo(nextMonth, fiscalCal),
      getMonthInfo(thirdMonth, fiscalCal)
    ].map((entry: MonthInfo) => ({
      ...entry,
      dates: entry.dates.map(date => {
        if (!date) {
          return date;
        }
        return {
          ...date,
          invalid: isOutsideRange(date.date) || isDayBlocked(date.date, focus)
        };
      })
    }));

    return { monthInfo: info, visibleMonthDigits: monthDigits };
  }, [selectedMonth, isOutsideRange, isDayBlocked, focus, currentMonthSecond, fiscalCal]);

  // Derived state during render: scroll calendar to show selected dates
  const [prevDateProps, setPrevDateProps] = useState({ start, end, fiscalCal });
  if (
    prevDateProps.start !== start ||
    prevDateProps.end !== end ||
    prevDateProps.fiscalCal !== fiscalCal
  ) {
    setPrevDateProps({ start, end, fiscalCal });

    if (start && end) {
      const startMonthYearStr = Dfns.format(
        YEAR_MONTH_FORMAT,
        getStartOfFiscalMonthLabel(start, fiscalCal)
      );
      const endMonthYearStr = Dfns.format(
        YEAR_MONTH_FORMAT,
        getStartOfFiscalMonthLabel(end, fiscalCal)
      );

      if (
        !visibleMonthDigits ||
        !visibleMonthDigits.has(startMonthYearStr) ||
        !visibleMonthDigits.has(endMonthYearStr)
      ) {
        const parsedToday = Dfns.parseISO(TODAY);
        const parsedStart = Dfns.parseISO(start);
        const parsedEnd = Dfns.parseISO(end);
        const diffInMoStartToEnd = differenceInFiscalMonths(parsedStart, parsedEnd, fiscalCal);
        const diffInMoStartToToday = differenceInFiscalMonths(parsedStart, parsedToday, fiscalCal);
        if (diffInMoStartToEnd === 0 && diffInMoStartToToday === 0) {
          setSelectedMonth(format(subFiscalMonths(2, parsedStart, fiscalCal)));
        } else if (diffInMoStartToToday < 2) {
          setSelectedMonth(format(subFiscalMonths(1, parsedStart, fiscalCal)));
        } else {
          setSelectedMonth(start);
        }
      }
    } else if (start) {
      const startMonthYearStr = Dfns.format(
        YEAR_MONTH_FORMAT,
        getStartOfFiscalMonthLabel(start, fiscalCal)
      );
      if (!visibleMonthDigits || !visibleMonthDigits.has(startMonthYearStr)) {
        setSelectedMonth(format(Dfns.parseISO(start)));
      }
    } else if (end) {
      const endMonthYearStr = Dfns.format(
        YEAR_MONTH_FORMAT,
        getStartOfFiscalMonthLabel(end, fiscalCal)
      );
      if (!visibleMonthDigits || !visibleMonthDigits.has(endMonthYearStr)) {
        setSelectedMonth(format(subFiscalMonths(2, Dfns.parseISO(end), fiscalCal)));
      }
    }
  }

  // All values here (including `end`) are month-start strings, matching what MonthGrid clicks
  // produce — onSelect's comparisons are generic string comparisons that work the same whether
  // the values represent days or month starts. Callers needing an actual end-of-month value
  // (e.g. for an API request) convert `end` themselves; the picker's own job is just "which
  // months," not "which exact days."
  const getMonthsForYear = useCallback(
    (year: number) => {
      const yearStart = getStartOfFiscalMonth(new Date(year, 0, 1), fiscalCal);
      return Array.from({ length: 12 }, (_, i) => {
        const value = format(addFiscalMonths(i, yearStart, fiscalCal));
        return { value, invalid: isOutsideRange(value) || isDayBlocked(value, focus) };
      });
    },
    [fiscalCal, isOutsideRange, isDayBlocked, focus]
  );

  // Side-by-side year panes — the year-grid equivalent of the day view's side-by-side months.
  const visibleYears = useMemo(() => {
    if (!fullMonthsOnly) return [];
    return Array.from({ length: numVisibleYears }, (_, i) => {
      const year = selectedYear + i;
      return { year, months: getMonthsForYear(year) };
    });
  }, [fullMonthsOnly, selectedYear, numVisibleYears, getMonthsForYear]);

  // Nothing past the current year is selectable, so don't let the right arrow scroll into a year
  // that's entirely blocked (e.g. wholly in the future).
  const nextYearFullyBlocked = useMemo(
    () => fullMonthsOnly && getMonthsForYear(selectedYear + numVisibleYears).every(m => m.invalid),
    [fullMonthsOnly, selectedYear, numVisibleYears, getMonthsForYear]
  );

  const adjustSelectedMonth = useCallback(
    (adjustment: number, date = Dfns.parseISO(selectedMonth || TODAY)) => {
      setSelectedMonth(format(addFiscalMonths(adjustment, date, fiscalCal)));
    },
    [selectedMonth, fiscalCal]
  );
  const adjustSelectedYear = useCallback(
    (adjustment: number) => setSelectedYear(y => y + adjustment),
    []
  );
  const onSelect = useCallback(
    (date: string) => {
      if (isSingle) {
        onChange({ start: date, end: date }, 'START');
        return;
      }
      if (!end && start) {
        const newRange: Partial<DateRange> = {};
        if (date < start) {
          newRange.start = date;
          newRange.end = start;
        } else if (date >= start) {
          newRange.start = start;
          newRange.end = date;
        } else {
          newRange.start = start;
        }
        onChange(newRange, 'START');
      } else if (!start && end) {
        const newRange: Partial<DateRange> = {};
        if (date <= end) {
          newRange.start = date;
          newRange.end = end;
        } else if (date > end) {
          newRange.start = end;
          newRange.end = date;
        } else {
          newRange.end = end;
        }
        onChange(newRange, 'END');
      } else if (start && end && end === start && date > start) {
        const newRange: Partial<DateRange> = {
          start,
          end: date
        };
        onChange(newRange, 'START');
      } else if (start && end && end === start && date < start) {
        const newRange: Partial<DateRange> = {
          start: date,
          end
        };
        onChange(newRange, 'END');
      } else if (date === start) {
        const newRange: Partial<DateRange> = {
          start,
          end: date
        };
        onChange(newRange, 'START');
      } else if (date === end) {
        const newRange: Partial<DateRange> = {
          start: date,
          end
        };
        onChange(newRange, 'END');
      } else if (focus === 'END') {
        const newRange: Partial<DateRange> = {
          end: date
        };
        if (!start || start <= date) {
          newRange.start = start;
        }
        onChange(newRange, 'START');
      } else {
        const newRange: Partial<DateRange> = {
          start: date
        };
        if (!end || end >= date) {
          newRange.end = end;
        }
        onChange(newRange, 'END');
      }
    },
    [start, end, focus, onChange, isSingle]
  );
  if (fullMonthsOnly) {
    return (
      <div className='dp-calendar'>
        {visibleYears.map(({ year, months }) => (
          <MonthGrid
            key={year}
            year={year}
            months={months}
            range={{ start, end }}
            onSelect={onSelect}
            focus={focus}
            classname={comparison ? 'comparison' : undefined}
          />
        ))}
        <IconButton
          className='nav-arrow left'
          design={Design.NEUTRAL}
          appearance={Appearance.EMPTY}
          onClick={() => adjustSelectedYear(-1)}
        >
          <MdChevronLeft />
        </IconButton>
        <IconButton
          className='nav-arrow right'
          design={Design.NEUTRAL}
          appearance={Appearance.EMPTY}
          disabled={nextYearFullyBlocked}
          onClick={() => adjustSelectedYear(1)}
        >
          <MdChevronRight />
        </IconButton>
      </div>
    );
  }

  return (
    <div className='dp-calendar'>
      {Array.from({ length: numVisibleMonths }, (_, i) => i).map(i => (
        <IndividualMonth
          key={`${monthInfo[i]?.date}`}
          monthInfo={monthInfo[i]}
          range={{ start, end }}
          onSelect={onSelect}
          focus={focus}
          setSelectedMonth={month => adjustSelectedMonth(-i, month)}
          years={years}
          maxDate={maxDate}
          classname={comparison ? 'comparison' : undefined}
          fiscalCal={fiscalCal}
        />
      ))}
      <IconButton
        className='nav-arrow left'
        design={Design.NEUTRAL}
        appearance={Appearance.EMPTY}
        onClick={() => adjustSelectedMonth(-1)}
      >
        <MdChevronLeft />
      </IconButton>
      <IconButton
        className='nav-arrow right'
        design={Design.NEUTRAL}
        appearance={Appearance.EMPTY}
        onClick={() => adjustSelectedMonth(1)}
      >
        <MdChevronRight />
      </IconButton>
    </div>
  );
};
